import { Badge, Button, Input, Select, Surface } from '@chopchop-se/ui';
import { useMutation } from '@tanstack/react-query';
import type { BarcodeResolution } from '@warehouse/shared';
import { ScanLine, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { isApiRequestError } from '../../../lib/api-error';
import { errorMessage } from '../../../lib/notify';
import { resolveBarcode } from '../api';
import { ReadOnlyField } from './form';
import { useSuppliers } from '../lookups';

/**
 * "Testa en skanning" — the resolution service made visible.
 *
 * This is the page that answers the spec's real question ("warehouse staff
 * don't know WHICH barcode to scan"): paste anything a supplier prints and see
 * exactly what the system will make of it — article, unit, quantity per scan,
 * lot, best-before, SSCC — plus the GS1 warnings, which is how a mis-encoded
 * supplier label gets diagnosed instead of blamed on the scanner.
 *
 * An unknown code is not an error state here; it is the entry point to the
 * teach flow.
 */
export function ScanPanel({
  onTeach,
}: {
  onTeach?: (raw: string, supplierId: string | null) => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { options: supplierOptions } = useSuppliers();
  const [raw, setRaw] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [result, setResult] = useState<BarcodeResolution | null>(null);
  const [ambiguous, setAmbiguous] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resolveBarcode(raw.trim(), supplierId || null),
    onMutate: () => {
      setResult(null);
      setAmbiguous(false);
      setFailure(null);
    },
    onSuccess: (resolution) => {
      setResult(resolution);
    },
    onError: (error: unknown) => {
      if (isApiRequestError(error) && error.code === 'E_BARCODE_AMBIGUOUS') {
        setAmbiguous(true);
        return;
      }
      setFailure(errorMessage(error));
    },
  });

  return (
    <Surface className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="flex items-center gap-2 font-display text-base font-semibold text-ink">
          <ScanLine className="size-4 text-brand-600" aria-hidden />
          {t('masterData:barcodes.scan.title')}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">{t('masterData:barcodes.scan.subtitle')}</p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (raw.trim().length > 0) mutation.mutate();
        }}
      >
        <Input
          variant="outline"
          className="w-full sm:w-96"
          value={raw}
          onChange={(event) => {
            setRaw(event.target.value);
          }}
          placeholder={t('masterData:barcodes.scan.placeholder')}
          aria-label={t('masterData:barcodes.scan.placeholder')}
          data-testid="scan-input"
        />
        <Select
          aria-label={t('masterData:barcodes.scan.supplier')}
          className="w-56"
          options={[{ value: '', label: t('masterData:barcodes.scan.anySupplier') }, ...supplierOptions]}
          value={supplierId}
          onChange={(event) => {
            setSupplierId(event.target.value);
          }}
        />
        <Button type="submit" loading={mutation.isPending} disabled={raw.trim().length === 0}>
          {t('masterData:barcodes.scan.submit')}
        </Button>
        {result || ambiguous || failure ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setRaw('');
              setResult(null);
              setAmbiguous(false);
              setFailure(null);
            }}
          >
            {t('masterData:barcodes.scan.clear')}
          </Button>
        ) : null}
      </form>

      {ambiguous ? (
        <Notice tone="warning" text={t('masterData:barcodes.scan.ambiguous')} />
      ) : null}
      {failure ? <Notice tone="danger" text={failure} /> : null}

      {result ? (
        result.matched && result.article ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="success" dot>
                {t('masterData:barcodes.scan.matched')}
              </Badge>
              <Badge tone="neutral" size="sm">
                {t(`masterData:barcodes.scan.sources.${result.source}`)}
              </Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.article')}
                value={
                  <Link className="text-brand-600 hover:underline" to={`/registers/articles/${result.article.id}`}>
                    {result.article.articleNo} — {result.article.name}
                  </Link>
                }
              />
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.unit')}
                value={result.unit ? `${result.unit.code} (${result.unit.name})` : null}
              />
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.qtyPerScan')}
                value={<span className="font-mono">{result.qtyPerScan}</span>}
              />
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.gtin')}
                value={result.scan.gtin14 ? <span className="font-mono">{result.scan.gtin14}</span> : null}
              />
              <ReadOnlyField label={t('masterData:barcodes.scan.result.lot')} value={result.lot} />
              <ReadOnlyField label={t('masterData:barcodes.scan.result.bestBefore')} value={result.bestBefore} />
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.sscc')}
                value={result.sscc ? <span className="font-mono">{result.sscc}</span> : null}
              />
              <ReadOnlyField
                label={t('masterData:barcodes.scan.result.warnings')}
                value={
                  result.scan.warnings.length === 0 ? null : (
                    <span className="flex flex-wrap gap-1">
                      {result.scan.warnings.map((warning) => (
                        <Badge key={warning} tone="warning" size="sm">
                          {warning}
                        </Badge>
                      ))}
                    </span>
                  )
                }
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Notice tone="warning" text={t('masterData:barcodes.scan.notMatchedHint')} title={t('masterData:barcodes.scan.notMatched')} />
            {onTeach ? (
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    onTeach(raw.trim(), supplierId || null);
                  }}
                >
                  {t('masterData:barcodes.scan.teach')}
                </Button>
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </Surface>
  );
}

function Notice({
  tone,
  text,
  title,
}: {
  tone: 'warning' | 'danger';
  text: string;
  title?: string;
}): ReactNode {
  const border = tone === 'danger' ? 'border-danger-600/30 bg-danger-600/[0.06]' : 'border-warning-600/30 bg-warning-600/[0.06]';
  return (
    <div role="alert" className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${border}`}>
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
      <div className="min-w-0 text-sm">
        {title ? <p className="font-medium text-ink">{title}</p> : null}
        <p className="text-ink-light">{text}</p>
      </div>
    </div>
  );
}
